import { useEffect, useState, useRef, useCallback } from "react";
import { Play, Pause, SkipBack, SkipForward, Music, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Poll every 5s when expanded, 8s when minimized.
// After a control action, re-poll after 600ms so the UI snaps to the new state.
const POLL_FAST_MS = 5000;
const POLL_SLOW_MS = 8000;
const CONTROL_REFRESH_MS = 600;

export default function SpotifyNowPlaying() {
  const [data, setData] = useState(null);
  const [tickProgress, setTickProgress] = useState(0);
  const [titleVariant, setTitleVariant] = useState(0);
  const [minimized, setMinimized] = useState(() => localStorage.getItem("cresc-spotify-min") === "1");
  const [optimisticPlaying, setOptimisticPlaying] = useState(null); // null = use server state
  const [playlistName, setPlaylistName] = useState("");
  const lastPoll = useRef(Date.now());
  const playlistCacheRef = useRef({});
  const pollRef = useRef(null); // holds current interval id so we can reset it

  const fetchNow = useCallback(async () => {
    try {
      const { data: res } = await api.get("/integrations/spotify/now-playing");
      if (!res?.connected) { setData(null); return; }
      setData(res);
      setOptimisticPlaying(null); // clear optimistic state once server responds
      lastPoll.current = Date.now();
      setTickProgress(0);
    } catch { setData(null); }
  }, []);

  // Reset polling interval — call this after control actions to get a fresh 5s window
  const resetPoll = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    const interval = minimized ? POLL_SLOW_MS : POLL_FAST_MS;
    pollRef.current = setInterval(fetchNow, interval);
  }, [minimized, fetchNow]);

  // Main polling loop
  useEffect(() => {
    fetchNow();
    const interval = minimized ? POLL_SLOW_MS : POLL_FAST_MS;
    pollRef.current = setInterval(fetchNow, interval);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [minimized, fetchNow]);

  // Re-poll when tab becomes visible (user switches back to the tab)
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) { fetchNow(); resetPoll(); } };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchNow, resetPoll]);

  // Smooth progress bar tick — only when playing
  useEffect(() => {
    if (!(optimisticPlaying ?? data?.is_playing)) return;
    const t = setInterval(() => setTickProgress(Date.now() - lastPoll.current), 200);
    return () => clearInterval(t);
  }, [data?.is_playing, optimisticPlaying, data?.item?.id]);

  // Alternating subtitle every 4s
  useEffect(() => {
    const id = setInterval(() => setTitleVariant((v) => (v + 1) % 2), 4000);
    return () => clearInterval(id);
  }, []);

  // Resolve playlist name once per URI
  useEffect(() => {
    const uri = data?.context?.uri;
    if (!uri || data?.context?.type !== "playlist") { setPlaylistName(""); return; }
    if (playlistCacheRef.current[uri]) { setPlaylistName(playlistCacheRef.current[uri]); return; }
    const id = uri.split(":").pop();
    if (!id) return;
    api.get(`/integrations/spotify/playlist/${id}`)
      .then(({ data: p }) => {
        const name = p?.name || "";
        playlistCacheRef.current[uri] = name;
        setPlaylistName(name);
      })
      .catch(() => setPlaylistName(""));
  }, [data?.context?.uri, data?.context?.type]);

  const toggleMin = useCallback(() => {
    setMinimized((cur) => {
      const next = !cur;
      localStorage.setItem("cresc-spotify-min", next ? "1" : "0");
      if (!next) {
        try { window.dispatchEvent(new CustomEvent("crescendo:spotify-expanded")); } catch {}
      }
      return next;
    });
  }, []);

  const control = useCallback(async (action) => {
    // Optimistic UI — immediately flip play/pause visually
    if (action === "play" || action === "pause") {
      setOptimisticPlaying(action === "play");
    }
    try {
      await api.post(`/integrations/spotify/control/${action}`);
    } catch (err) {
      setOptimisticPlaying(null); // revert on error
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      const msg = typeof d === "object" ? d.message : (d || "Spotify control failed");
      if (status === 403) {
        toast.error("Reconnect Spotify in the Library to enable playback control.");
      } else {
        toast.error(msg);
      }
      return;
    }
    // After action, wait a moment for Spotify to process then re-fetch
    setTimeout(() => { fetchNow(); resetPoll(); }, CONTROL_REFRESH_MS);
  }, [fetchNow, resetPoll]);

  if (!data || !data.connected || !data.item) return null;

  const item = data.item;
  const isPlaying = optimisticPlaying ?? data.is_playing;
  const progressMs = (data.progress_ms || 0) + (isPlaying ? tickProgress : 0);
  const total = item.duration_ms || 1;
  const pct = Math.min(100, Math.max(0, (progressMs / total) * 100));

  const artist = (item.artists || []).join(", ");
  const ctxType = data.context?.type;
  const ctxUri = data.context?.uri;
  let ctxLabel = null;
  if (ctxType === "collection" || ctxUri?.includes("collection")) {
    ctxLabel = "Playing from Liked Songs";
  } else if (ctxType === "playlist") {
    ctxLabel = playlistName ? `Playing from ${playlistName}` : "Playing from a playlist";
  } else if (ctxType === "album") {
    ctxLabel = item.album ? `Playing from ${item.album}` : "Playing from album";
  } else if (ctxType === "artist") {
    ctxLabel = `Playing from ${artist}`;
  } else if (!ctxType && !ctxUri) {
    ctxLabel = item.album ? `Playing from ${item.album}` : null;
  }
  const showCtx = !!ctxLabel && titleVariant === 1;
  const subtitle = showCtx ? ctxLabel : artist;

  const easeStyle = "max-height 700ms cubic-bezier(0.4, 0, 0.2, 1), padding 700ms cubic-bezier(0.4, 0, 0.2, 1)";

  return (
    <div className="border-t border-[#2A2A2A] bg-[#0F0F0F] flex flex-col" data-testid="spotify-now-playing">
      {/* Album art */}
      <div className="relative overflow-hidden bg-[#1a1a1a]" style={{ maxHeight: minimized ? 0 : "100vw", transition: easeStyle }}>
        <div className="aspect-square">
          {item.album_art
            ? <img src={item.album_art} alt="" className="w-full h-full object-cover" />
            : <Music className="w-10 h-10 text-[#52525B] absolute inset-0 m-auto" />}
        </div>
        {!minimized && (
          <button onClick={toggleMin}
            className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-black/60 backdrop-blur text-white hover:bg-black/80 flex items-center justify-center"
            title="Minimize" data-testid="spotify-minimize">
            <ChevronDown className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Subtitle */}
      <div className="overflow-hidden" style={{ maxHeight: minimized ? 0 : 32, transition: easeStyle }}>
        <div className="px-2.5 pt-2">
          <div className="text-[9px] uppercase tracking-wider text-[#5A90F5] truncate" data-testid="spotify-subtitle" title={subtitle}>
            {subtitle || "\u00A0"}
          </div>
        </div>
      </div>

      {/* Title + controls */}
      <div className="flex items-center gap-2 px-2.5 py-1.5">
        {minimized && (
          <div className="relative w-6 h-6 rounded flex-shrink-0 overflow-hidden bg-[#1a1a1a]">
            {item.album_art ? <img src={item.album_art} alt="" className="w-full h-full object-cover" /> : <Music className="w-3 h-3 text-[#52525B] m-auto" />}
          </div>
        )}
        <div className="flex-1 min-w-0 text-[11px] font-semibold truncate text-[#EDEDED]" data-testid="spotify-title">
          {item.title}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!minimized && (
            <button onClick={() => control("previous")}
              className="p-0.5 rounded text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#1a1a1a]"
              title="Previous" data-testid="spotify-prev">
              <SkipBack className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={() => control(isPlaying ? "pause" : "play")}
            className="p-0.5 text-white hover:text-[#EDEDED] hover:scale-110 transition-transform"
            title={isPlaying ? "Pause" : "Play"} data-testid="spotify-toggle">
            {isPlaying ? <Pause className="w-3.5 h-3.5" fill="currentColor" /> : <Play className="w-3.5 h-3.5" fill="currentColor" />}
          </button>
          {!minimized && (
            <button onClick={() => control("next")}
              className="p-0.5 rounded text-[#A1A1AA] hover:text-[#EDEDED] hover:bg-[#1a1a1a]"
              title="Next" data-testid="spotify-next">
              <SkipForward className="w-3 h-3" />
            </button>
          )}
          {minimized && (
            <button onClick={toggleMin}
              className="p-0.5 text-[#A1A1AA] hover:text-[#EDEDED] flex-shrink-0"
              title="Expand" data-testid="spotify-expand">
              <ChevronUp className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-0.5 w-full bg-[#2A2A2A] overflow-hidden" data-testid="spotify-progress">
        <div className="h-full bg-white transition-all duration-200" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

